import { useEffect, useRef } from "react";
import { useSubscribeToEvent, useTriggerAlert, useAddToast } from "src/context/SocketContext";

const useSocketListeners = (listeners = []) => {
  const subscribeToEvent = useSubscribeToEvent();
  const triggerAlert = useTriggerAlert();
  const addToast = useAddToast();

  const listenersRef = useRef(listeners);
  listenersRef.current = listeners;

  useEffect(() => {
    if (!listeners.length) return;

    const cleanups = listeners.map((config, index) => {
      const { event } = config;
      if (!event) return null;

      const wrappedHandler = (data) => {
        const { handler, condition, critical = false, message, title, variant = "info", autoApply = false, onSync } = listenersRef.current[index] ?? {};

        if (condition && !condition(data)) return;

        if (critical) {
          triggerAlert({
            title: title || "System update",
            message: message || "Changes detected. Click to sync.",
            onConfirm: () => (handler ? handler(data) : window.location.reload()),
          });
          return;
        }

        if (message) {
          addToast({
            event,
            data,
            title,
            message,
            variant,
            autoApply,
            onAction: async () => {
              try {
                if (handler) {
                  const result = await handler(data);
                  if (onSync) onSync(result);
                }
              } catch (err) {
                console.error(`Sync failed for ${event}:`, err);
                throw err;
              }
            },
          });
        } else if (handler) {
          handler(data);
        }
      };

      return subscribeToEvent(event, wrappedHandler);
    });

    return () => cleanups.forEach((fn) => fn?.());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useSocketListeners;
